import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { softDeleteEvent, updateEventStatus, upsertEventDetail } from '../../data/events';
import { eventDetailQueryKey, eventsListQueryKey } from '../../domain/events/useEvents';
import supabaseClient from '../../lib/supabaseClient';
import type { EventDetail } from '../../domain/events';
import { invalidateAvailabilityQueries } from './useInventoryMutations';

export function useEventMutations(supabase: SupabaseClient = supabaseClient) {
  const queryClient = useQueryClient();

  const updateStatus = useMutation({
    mutationFn: async ({ eventId, status }: { eventId: string; status: string }) => {
      const error = await updateEventStatus({ eventId, status, supabase });
      if (error) throw error;
      return { eventId, status };
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(variables.eventId) });
      void queryClient.invalidateQueries({ queryKey: eventsListQueryKey() });
      invalidateAvailabilityQueries(queryClient);
    },
  });

  const upsertDetail = useMutation({
    mutationFn: async (detail: EventDetail) => {
      const { data, error } = await upsertEventDetail({ detail, supabase });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: eventsListQueryKey() });
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(data?.id ?? null) });
      invalidateAvailabilityQueries(queryClient);
    },
  });

  const softDelete = useMutation({
    mutationFn: async (eventId: string) => {
      const error = await softDeleteEvent({ eventId, supabase });
      if (error) throw error;
      return eventId;
    },
    onSuccess: (eventId) => {
      void queryClient.invalidateQueries({ queryKey: eventsListQueryKey() });
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
      invalidateAvailabilityQueries(queryClient);
    },
  });

  return { updateStatus, upsertDetail, softDelete };
}
